<template>
    <div class="invite-guide">
        <GuideView
            ref="guideViewRef"
            v-if="inited"
            :channel-configs="channelConfigsSorted"
            :image-configs="imageConfigs"
            :desc-configs="descConfigs"
            :market="market"
            :inviteConfigs="inviteConfigs"
            :shareTitle="shareTitle"
            :channelDisplayLayout="channelDisplayLayout"
            :reportAttrs="reportAttrs"
            @shareSuccess="shareSuccess"
        ></GuideView>
        <ForbiddenModal v-if="forbiddenType" :forbidden-type="forbiddenType" :market="market"> </ForbiddenModal>
    </div>
</template>

<!-- impact上报组件，指定多元归因参数下才会加载第三方sdk并上报 -->
<script >
import { isAndroid, isIOS } from '@futuweb/tool-browser';
import NN from '@futuweb/tool-nn';
import { computed, onMounted, ref, useAttrs } from 'vue-demi';
import { fetchProfitImage } from '../common/app';
import { BEHAVIOR_EVENT, EXPOSE_FEGO_METHOD, FORBIDDEN_TYPE, IMAGE_TYPE } from '../common/constants';
import { _lang, watchLanguage } from '../common/lang';
import log from '../common/log';
import { enableChannels, getEnableChannel, isApp, isLowVersion } from '../common/share';
import { activeChannel } from '../common/store';
import { Channel, ChannelSortType, Language } from '../common/type';
import ForbiddenModal from '../components/forbidden-modal.vue';
import GuideView from '../views/GuideView.vue';
const __sfc_main = {};
__sfc_main.props = {
  // 语种
  language: {
    type: String,
    default: Language.EN
  },
  market: {
    type: String,
    default: ''
  },
  imageConfigs: {
    type: Array,
    default: () => []
  },
  descConfigs: {
    type: Array,
    default: () => []
  },
  channelConfigs: {
    type: Array,
    default: () => []
  },
  disableSwiper: {
    type: Boolean,
    default: true
  },
  channelDisplayLayout: {
    type: String,
    default: undefined
  },
  channelSortType: {
    type: String,
    default: ChannelSortType.CUSTOM
  },
  // 简化模式字段（兼容处理，通常已在保存时转换）
  simpleChannels: {
    type: Array,
    default: undefined
  },
  simpleImageIds: {
    type: Array,
    default: undefined
  },
  simpleShowShareDesc: {
    type: Boolean,
    default: undefined
  },
  simpleShowChannelTip: {
    type: Boolean,
    default: undefined
  },
  simpleChannelTip: {
    type: String,
    default: undefined
  },
  inviteConfigs: {
    type: Array,
    default: () => []
  },
  shareTitle: {
    type: String,
    default: _lang('shareTitle')
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const reportAttrs = computed(() => {
    return {
      element_id: useAttrs()['data-id'] || '',
      element_name: useAttrs()['data-name'] || ''
    };
  });
  const props = __props;
  const emits = __ctx.emit;
  watchLanguage(() => props.language);

  // 兼容简化模式：如果传入了简化字段，进行转换
  const channelConfigsResolved = computed(() => {
    // 如果是简化模式且存在简化字段（兼容旧数据或转换失败的情况）
    if (props.channelDisplayLayout === 'bottomShareBtn' && props.simpleChannels?.length > 0) {
      return props.simpleChannels.map(channel => ({
        channel,
        image_ids: props.simpleImageIds || [],
        showShareDesc: props.simpleShowShareDesc !== false,
        showChannelTip: props.simpleShowChannelTip || false,
        channelTip: props.simpleChannelTip
      }));
    }
    // 默认使用标准的 channelConfigs
    return props.channelConfigs || [];
  });

  // 渠道列表排序和过滤
  const channelConfigsSorted = computed(() => {
    const listBefore = [];
    const listAfter = [];
    for (const channelConfig of channelConfigsResolved.value) {
      const filtered = {
        ...channelConfig,
        image_ids: channelConfig.image_ids.filter(id => {
          return props.imageConfigs.some(config => config.url && id === config.id);
        })
      };
      // 过滤出有值的图片id
      if (!filtered.image_ids.length) {
        continue;
      }
      // 自定义排序
      if (props.channelSortType === ChannelSortType.CUSTOM) {
        listBefore.push(filtered);
      } else {
        // 按APP安装排序
        // 优先展示已开启的渠道, 未开启的渠道放在后面
        if (enableChannels.value.includes(filtered.channel)) {
          listBefore.push(filtered);
        } else {
          listAfter.push(filtered);
        }
      }
    }
    return [...listBefore, ...listAfter];
  });

  // 屏蔽弹窗
  const forbiddenType = ref('');

  // 子组件引用
  const guideViewRef = ref(null);
  const inited = ref(false);
  onMounted(async () => {
    // 预渲染下不执行
    if (window.FEGORender?.common?.env?.isPreRender) {
      return;
    }
    const isMobile = isAndroid() || isIOS();
    if (!isApp() || !isMobile) {
      // 不是moomoo nn 或者 不是 安卓、ios
      forbiddenType.value = FORBIDDEN_TYPE.NOT_APP;
      return;
    }
    if (isLowVersion()) {
      forbiddenType.value = FORBIDDEN_TYPE.LOW_VERSION;
      return;
    }
    if (props.disableSwiper) {
      NN.disableSwiper();
    } else {
      NN.enableSwiper();
    }
    await Promise.all([getEnableChannel(), getProfitImages()]);
    inited.value = true;
  });
  function setActiveChannel(channel) {
    const index = channelConfigsSorted?.value?.findIndex(item => item.channel === channel);
    if (index !== -1) {
      activeChannel.value = index;
    }
  }
  /**
   * 获取盈亏分享图
   */
  async function getProfitImages() {
    const types = new Set();
    props.imageConfigs.forEach(item => {
      if (item.type && item.type !== IMAGE_TYPE.STATIC) {
        // 过滤出有值且非静态图的类型
        types.add(item.type);
      }
    });
    if (types.size <= 0) {
      return;
    }
    const imageMap = {};
    await Promise.all(Array.from(types).map(async type => {
      try {
        imageMap[type] = await fetchProfitImage({
          type
        });
      } catch (error) {
        log.error('getProfitImages catch', {
          type,
          error
        });
      }
    }));
    props.imageConfigs.forEach(item => {
      if (item.type && item.type !== IMAGE_TYPE.STATIC) {
        // 过滤出有值且非静态图的类型
        // 重写url
        // 拉失败就展示兜底图
        item.url = imageMap[item.type] || '';
      }
    });
  }
  function shareSuccess() {
    emits(BEHAVIOR_EVENT.SHARE_SUCCESS);
  }
  return Object.assign({
    reportAttrs,
    channelConfigsSorted,
    forbiddenType,
    guideViewRef,
    inited,
    shareSuccess
  }, {
    [EXPOSE_FEGO_METHOD.SHOW_XIAOHONGSHU_SHARE]: () => {
      setActiveChannel(Channel.XIAOHONGSHU);
      guideViewRef.value?.doShare();
    },
    [EXPOSE_FEGO_METHOD.SHOW_REDDIT_SHARE]: () => {
      setActiveChannel(Channel.REDDIT);
      guideViewRef.value?.doShare();
    },
    [EXPOSE_FEGO_METHOD.SHOW_FACEBOOK_SHARE]: () => {
      setActiveChannel(Channel.FACEBOOK);
      guideViewRef.value?.doShare();
    },
    [EXPOSE_FEGO_METHOD.SHOW_TELEGRAM_SHARE]: () => {
      setActiveChannel(Channel.TELEGRAM);
      guideViewRef.value?.doShare();
    },
    [EXPOSE_FEGO_METHOD.SHOW_TWITTER_SHARE]: () => {
      setActiveChannel(Channel.TWITTER);
      guideViewRef.value?.doShare();
    },
    [EXPOSE_FEGO_METHOD.SHOW_INSTAGRAM_SHARE]: () => {
      setActiveChannel(Channel.INSTAGRAM);
      guideViewRef.value?.doShare();
    },
    [EXPOSE_FEGO_METHOD.SHOW_SYSTEM_SHARE]: () => {
      setActiveChannel(Channel.SYSTEM);
      guideViewRef.value?.doShare();
    }
  });
};
__sfc_main.components = Object.assign({
  GuideView,
  ForbiddenModal
}, __sfc_main.components);
export default __sfc_main;
</script>

<style lang="less" scoped>
.invite-guide {
    height: auto !important;
}
</style>
